import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectPlayerList } from '@/store/slices/ownerHubStats';

import { buildCasinoSettings } from '../../../../mock-data/casino-settings';
import { ROUTE } from '../../../../routes';
import { fetchLogoFamily, fetchRunStatus, logoApiConfigured, type LogoFamily } from '../../../casino-settings/logo-api';
import { isRunResumable, readStoredRun, type StoredRun } from '../../../casino-settings/logo-replacement';
import {
  applyProvenance,
  logoSetSource,
  logoStepState,
  resolveLogos,
} from '../../../casino-settings/logos';
import { useLogoDownloads } from '../../../logos/useLogoDownloads';
import type { RawRow } from '../../../players/roster-merge';
import { resolveSummary, unaddressableView, type WebsiteCardView } from '../../card-summary';
import { useBrandSettled } from '../../useBrandSettled';
import { useWebsiteHubChrome } from '../../hub-chrome';
import { lockFor, readLogoLock } from './logo-lock-read';
import { logosCopy } from './logos-copy';
import { deriveLogosSummary, type LogoLock, type LogosCardInput } from './logos-summary';

/** What the reads returned, stamped with the tenant they were made for. */
type Reads = {
  tenantId: number;
  status: LogosCardInput['status'];
  family: LogoFamily | undefined;
  /** The family read failed (the card then goes by the brand record's map, as the Logos page does). */
  familyFailed: boolean;
  run: LogosCardInput['run'];
};

/** With no logo API there is nothing to read: the logos come from the brand record, as on the Logos page. */
const NO_READS = { status: 'ready', family: undefined, familyFailed: false, run: 'none' } as const;

/** One status read of the stored run: over and failed, still going, or nothing to report (a failed read included). */
const readRun = async (run: StoredRun, tenantId: number, signal: AbortSignal): Promise<LogosCardInput['run']> => {
  try {
    const status = await fetchRunStatus(run.pipelineId, tenantId, signal);
    if (status.failed) return 'failed';
    return status.settled ? 'none' : 'running';
  } catch {
    return 'none';
  }
};

/**
 * The Website Logos card: reads what the Logos page opens with (the logo family, falling back to the brand record's
 * map), and, when this browser left a replacement run in flight, that run's status once: no polling, no resume, the
 * Logos page follows the run. The lock is the page's own 50-player rule: over the registration list the Players or
 * Logos page left in the store, else over as few pages of it as decide the rule (`logo-lock-read`). "Download all"
 * builds the same zip as the page, through the hook both share. Reads of a superseded brand are aborted and dropped.
 */
export const useLogosCard = (): WebsiteCardView => {
  const { t } = useTranslation();
  const { notify } = useWebsiteHubChrome();
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandSettled = useBrandSettled();
  const parsedTenant = Number(displayBrand?.tenant_id);
  const tenantId = Number.isInteger(parsedTenant) && parsedTenant > 0 ? parsedTenant : undefined;
  const configured = logoApiConfigured();

  const [reads, setReads] = React.useState<Reads | null>(null);

  React.useEffect(() => {
    if (tenantId === undefined || !configured) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      const stored = readStoredRun(tenantId);
      const noRun: LogosCardInput['run'] = 'none';
      const [family, run] = await Promise.all([
        fetchLogoFamily(tenantId, signal).then(
          (value): LogoFamily | 'failed' => value,
          (): 'failed' => 'failed',
        ),
        stored && isRunResumable(stored, Date.now()) ? readRun(stored, tenantId, signal) : noRun,
      ]);
      if (signal.aborted) return;
      // A failed family read falls back to the brand record's map, as on the Logos page (see `logoSet`). With no map
      // to fall back to, it is a failed read, never "no logos" (see `status` below).
      const familyFailed = family === 'failed';
      setReads({
        tenantId,
        status: 'ready',
        family: familyFailed ? undefined : family ?? undefined,
        familyFailed,
        run,
      });
    })();
    return () => controller.abort();
  }, [tenantId, configured]);

  // Until this tenant's reads land, the card shows them as loading, never the previous brand's.
  const current = !configured
    ? NO_READS
    : reads && reads.tenantId === tenantId
      ? reads
      : { status: 'loading' as const, family: undefined, familyFailed: false, run: 'none' as const };

  const fallback = React.useMemo(() => buildCasinoSettings(0), []);
  const brandName = displayBrand?.casino_data?.details?.brand || fallback.casinoName;
  const { family } = current;
  const logoSet = React.useMemo(
    () =>
      applyProvenance(
        resolveLogos(logoSetSource(family, displayBrand), fallback.logos, brandName),
        family,
        undefined,
      ),
    [family, displayBrand, fallback.logos, brandName],
  );

  const casinoInstanceId = displayBrand?.id || undefined;
  // The owner's token, as the roster's own read sends it; only needed (and only read) once there is a brand to ask about.
  const token = useAppSelector((state) => (casinoInstanceId ? state.authentication.access_token : null));
  const cachedList = useAppSelector((state) => selectPlayerList(state, casinoInstanceId));
  const [lockRead, setLockRead] = React.useState<{ casinoInstanceId: string; lock: LogoLock } | null>(null);

  React.useEffect(() => {
    if (casinoInstanceId === undefined || cachedList) return undefined;
    const controller = new AbortController();
    void readLogoLock(casinoInstanceId, token, controller.signal).then((verdict) => {
      if (!controller.signal.aborted) setLockRead({ casinoInstanceId, lock: verdict });
    });
    return () => controller.abort();
  }, [casinoInstanceId, cachedList, token]);

  // Until this brand's verdict is in, the lock is pending, never the previous brand's.
  const lock: LogoLock = cachedList
    ? lockFor(Array.isArray(cachedList.data) ? (cachedList.data as RawRow[]) : [])
    : lockRead && lockRead.casinoInstanceId === casinoInstanceId
      ? lockRead.lock
      : 'pending';

  const { downloadAll } = useLogoDownloads({ logoSet, brandName, notify });

  if (tenantId === undefined) {
    return unaddressableView(brandSettled, { label: t('Open logos'), to: ROUTE.logos() });
  }
  return resolveSummary(
    deriveLogosSummary({
      // The Logos page shows its error then: the family read failed and the brand record has no logos of its own.
      status: current.familyFailed && logoSet.source !== 'brand' ? 'failed' : current.status,
      logos: {
        source: logoSet.source,
        slots: logoSet.source === 'brand' ? [logoSet.primary, ...logoSet.variants].map((logo) => logo.id) : [],
      },
      step: logoStepState(displayBrand),
      run: current.run,
      lock,
    }),
    (c) => logosCopy(t, c),
    // The one handler the summary can name.
    (name) => (name === 'download-all' ? downloadAll : undefined),
  );
};
